import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import logoAsset from "../assets/logo.jpeg";

import {
  MdDashboard,
  MdStore,
  MdSubscriptions,
  MdCardMembership,
  MdPayment,
  MdCategory,
  MdClass,
  MdShoppingBag,
  MdAccountBalanceWallet,
  MdLogout,
  MdMenu,
  MdClose,
  MdPerson,
  MdSearch,
  MdCampaign,
  MdTune,
  MdStoreMallDirectory
} from "react-icons/md";


export default function OpticalAdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [pendingStoreCount, setPendingStoreCount] = useState(0);

  const adminData = JSON.parse(localStorage.getItem("admin") || "{}");

  useEffect(() => {
    const fetchPendingStores = async () => {
      try {
        const res = await fetch("https://api.visiontrackcrm.com/registration/stores");
        const data = await res.json();
        if (data?.success && Array.isArray(data.data)) {
          const pending = data.data.filter(
            (s) => String(s.subscription_status || s.status || "").toUpperCase() === "PENDING"
          ).length;
          setPendingStoreCount(pending);
        }
      } catch (e) {
        // background silent fail
      }
    };

    fetchPendingStores();
    const interval = setInterval(fetchPendingStores, 30000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const logout = () => {
    localStorage.removeItem("admin");
    localStorage.removeItem("adminToken");
    localStorage.removeItem("token");
    sessionStorage.clear();
    setSidebarOpen(false);
    navigate("/adminlogin", { replace: true });
  };

  const closeMenu = () => {
    if (isMobile) setSidebarOpen(false);
  };

  // Format title based on path
  const getPageTitle = () => {
    const path = location.pathname.split("/").pop();
    if (!path || path === "dashboard") return "Dashboard Overview";
    return path.charAt(0).toUpperCase() + path.slice(1).replace(/-/g, " ");
  };

  const navSections = [
    {
      title: "OVERVIEW",
      items: [
        { path: "/admin/dashboard", label: "Dashboard", icon: <MdDashboard /> },
      ]
    },
    {
      title: "STORE MANAGEMENT",
      items: [
        { path: "/admin/store-directory", label: "Store Directory", icon: <MdStoreMallDirectory /> },
        { path: "/admin/stores", label: "Store Management", icon: <MdStore /> },
        { path: "/admin/subscriptions", label: "Subscription Approval", icon: <MdSubscriptions />, badge: pendingStoreCount },
        { path: "/admin/subscriptionsplans", label: "Subscription Plans", icon: <MdCardMembership /> },
        { path: "/admin/Paymenthistory", label: "Payment History", icon: <MdPayment /> },
      ]
    },
    {
      title: "CATALOG",
      items: [
        { path: "/admin/categories", label: "Categories", icon: <MdCategory /> },
        { path: "/admin/brands", label: "Brands", icon: <MdClass /> },
        { path: "/admin/products", label: "Products Catalog", icon: <MdShoppingBag /> },
        { path: "/admin/ecommerce-cms", label: "E-Commerce & Banners CMS", icon: <MdCampaign /> },
      ]
    },

    {
      title: "CLINICAL",
      items: [
        { path: "/admin/clinical-dropdowns", label: "Dropdown Master Lists", icon: <MdTune /> },
      ]
    },
    {
      title: "FINANCE",
      items: [
        { path: "/admin/expensetracker", label: "Expense Tracker", icon: <MdAccountBalanceWallet /> },
      ]
    }
  ];

  return (
    <>
      <style>{`
        .layout-container {
          display: flex;
          height: 100vh;
          width: 100vw;
          overflow: hidden;
          background-color: #f4f7fc;
          font-family: 'Inter', sans-serif;
        }

        /* SIDEBAR STYLES */
        .sidebar {
          width: 270px;
          height: 100vh;
          background: #ffffff;
          border-right: 1px solid #e2e8f0;
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          z-index: 100;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 4px 0 24px rgba(0, 0, 0, 0.02);
        }

        .sidebar-header {
          padding: 20px 24px;
          display: flex;
          align-items: center;
          gap: 14px;
          border-bottom: 1px solid #f1f5f9;
        }

        .logo-img {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          object-fit: cover;
          box-shadow: 0 4px 10px rgba(0, 103, 177, 0.2);
          border: 2px solid #ffffff;
        }

        .brand-info {
          display: flex;
          flex-direction: column;
        }

        .brand-name {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-weight: 800;
          font-size: 1.05rem;
          color: #0f172a;
          letter-spacing: -0.02em;
        }

        .brand-badge {
          font-size: 0.7rem;
          font-weight: 700;
          color: #0067B1;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .sidebar-nav {
          flex: 1;
          overflow-y: auto;
          padding: 16px 14px;
        }

        .nav-section-title {
          font-size: 0.68rem;
          font-weight: 700;
          color: #94a3b8;
          letter-spacing: 0.08em;
          padding: 12px 12px 6px 12px;
          margin-top: 4px;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 14px;
          margin-bottom: 3px;
          border-radius: 10px;
          text-decoration: none;
          color: #475569;
          font-size: 0.88rem;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .nav-item-icon {
          font-size: 1.25rem;
          color: #64748b;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s ease;
        }

        .nav-item:hover {
          background: #f1f5f9;
          color: #0067B1;
        }

        .nav-item:hover .nav-item-icon {
          color: #0067B1;
        }

        .nav-item.active {
          background: linear-gradient(135deg, #0067B1 0%, #1e40af 100%);
          color: #ffffff;
          font-weight: 600;
          box-shadow: 0 4px 12px rgba(0, 103, 177, 0.25);
        }

        .nav-item.active .nav-item-icon {
          color: #ffffff;
        }

        .nav-badge-pill {
          margin-left: auto;
          background: #ef4444;
          color: #ffffff;
          font-size: 0.72rem;
          font-weight: 700;
          padding: 2px 7px;
          border-radius: 12px;
          min-width: 18px;
          text-align: center;
          box-shadow: 0 2px 5px rgba(239, 68, 68, 0.35);
          line-height: 1.2;
        }

        .nav-item.active .nav-badge-pill {
          background: #ffffff;
          color: #ef4444;
        }

        .sidebar-footer {
          padding: 16px 20px;
          border-top: 1px solid #f1f5f9;
          background: #fafafa;
        }

        .user-profile {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
        }

        .user-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #e0efff;
          color: #0067B1;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.95rem;
        }

        .user-details {
          flex: 1;
          overflow: hidden;
        }

        .user-name {
          font-size: 0.85rem;
          font-weight: 600;
          color: #0f172a;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .user-role {
          font-size: 0.72rem;
          color: #64748b;
        }

        .logout-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 9px;
          background: #fff0f0;
          color: #e11d48;
          border: 1px solid #fecdd3;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .logout-btn:hover {
          background: #e11d48;
          color: #ffffff;
          border-color: #e11d48;
        }

        /* MAIN CONTENT CONTAINER */
        .main-wrapper {
          flex: 1;
          height: 100vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        /* TOP HEADER BAR */
        .topbar {
          height: 64px;
          background: #ffffff;
          border-bottom: 1px solid #e2e8f0;
          padding: 0 28px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
          z-index: 10;
        }

        .topbar-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .mobile-toggle {
          display: none;
          background: transparent;
          border: none;
          font-size: 1.5rem;
          color: #334155;
          cursor: pointer;
          padding: 4px;
        }

        .page-heading {
          font-size: 1.2rem;
          font-weight: 700;
          color: #0f172a;
        }

        .topbar-right {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .topbar-search {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          padding: 6px 14px;
          border-radius: 20px;
          width: 220px;
        }

        .topbar-search input {
          border: none;
          background: transparent;
          outline: none;
          font-size: 0.85rem;
          width: 100%;
          color: #334155;
        }

        .status-pill {
          display: flex;
          align-items: center;
          gap: 6px;
          background: #ecfdf5;
          color: #059669;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 4px 12px;
          border-radius: 9999px;
          border: 1px solid #a7f3d0;
        }

        .status-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #10b981;
        }

        /* SCROLLABLE PAGE CONTENT AREA */
        .content-area {
          flex: 1;
          height: calc(100vh - 64px);
          overflow-y: auto;
          padding: 28px 32px;
          background-color: #f4f7fc;
        }

        /* OVERLAY FOR MOBILE */
        .sidebar-overlay {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(15, 23, 42, 0.4);
          backdrop-filter: blur(4px);
          z-index: 90;
        }

        /* RESPONSIVE DESIGN */
        @media (max-width: 1024px) {
          .sidebar {
            position: fixed;
            top: 0;
            left: 0;
            transform: translateX(-100%);
          }

          .sidebar.open {
            transform: translateX(0);
          }

          .sidebar-overlay.show {
            display: block;
          }

          .mobile-toggle {
            display: flex;
          }

          .topbar-search {
            display: none;
          }

          .content-area {
            padding: 20px;
          }
        }
      `}</style>

      <div className="layout-container">
        {/* Mobile Backdrop Overlay */}
        <div
          className={`sidebar-overlay ${sidebarOpen ? "show" : ""}`}
          onClick={() => setSidebarOpen(false)}
        />

        {/* Pinned / Sticky Sidebar */}
        <aside className={`sidebar ${sidebarOpen ? "open" : ""}`}>
          <div className="sidebar-header">
            <img src={logoAsset} alt="VisionTrack Logo" className="logo-img" />
            <div className="brand-info">
              <span className="brand-name">VisionTrack</span>
              <span className="brand-badge">Super Admin</span>
            </div>
          </div>

          <nav className="sidebar-nav">
            {navSections.map((section, idx) => (
              <div key={idx}>
                <div className="nav-section-title">{section.title}</div>
                {section.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={closeMenu}
                    className={({ isActive }) =>
                      `nav-item ${isActive ? "active" : ""}`
                    }
                  >
                    <span className="nav-item-icon">{item.icon}</span>
                    <span>{item.label}</span>
                    {Boolean(item.badge && item.badge > 0) && (
                      <span className="nav-badge-pill">{item.badge}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>

          <div className="sidebar-footer">
            <div className="user-profile">
              <div className="user-avatar">
                {adminData.name ? adminData.name.charAt(0).toUpperCase() : <MdPerson />}
              </div>
              <div className="user-details">
                <div className="user-name">{adminData.name || "Super Admin"}</div>
                <div className="user-role">{adminData.email || "admin@visiontrack.com"}</div>
              </div>
            </div>

            <button className="logout-btn" onClick={logout}>
              <MdLogout />
              <span>Logout</span>
            </button>
          </div>
        </aside>

        {/* Main Content Area (Independent Smooth Scroll) */}
        <div className="main-wrapper">
          <header className="topbar">
            <div className="topbar-left">
              <button
                className="mobile-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-label="Toggle Sidebar"
              >
                {sidebarOpen ? <MdClose /> : <MdMenu />}
              </button>
              <h1 className="page-heading">{getPageTitle()}</h1>
            </div>

            <div className="topbar-right">
              <div className="topbar-search">
                <MdSearch style={{ color: "#94a3b8", fontSize: "1.1rem" }} />
                <input type="text" placeholder="Quick search..." />
              </div>
              <div className="status-pill">
                <span className="status-dot"></span>
                <span>System Online</span>
              </div>
            </div>
          </header>

          <main className="content-area">
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
}